import CircularLoader from 'components/CircularLoader.tsx';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { useAdornmentGetters } from 'components/forms/inputs/rhf-inputs/useAdornmentGetters.ts';
import { usePaginatedPopup } from 'components/forms/inputs/rhf-inputs/usePaginatedList.ts';
import { Checkbox } from 'components/ui/checkbox';
import {
    Combobox,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxLabel,
    ComboboxList,
    ComboboxSeparator,
    ComboboxTrigger,
    useComboboxAnchor,
} from 'components/ui/combobox';
import { InputGroupButton } from 'components/ui/input-group';
import { Skeleton } from 'components/ui/skeleton';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll.ts';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { groupConsecutiveOptions, OptionGroup } from 'utils/autocompleteGrouping';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { getSelectorKey } from '../../getSelectorKey.ts';
import {
    DynamicMultiselectFilterForm,
    isEmptinessOption,
    MainFilterForm,
    MainFilterInForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    QuickFilterForm,
    SelectOption,
} from '../../ListTableTypes.tsx';
import { MIN_SEARCH_LENGTH_FOR_SELECT_ALL } from '../inputConstants.ts';
import {
    checkIfSomeFilteredOptionNotSelected,
    checkIfSomeFilteredOptionSelected,
    mergeOptions,
    separateOptions,
} from '../utils.ts';
import { FilterLabel } from './FilterLabel';
import {
    CheckboxOptionRow,
    CollapsedTags,
    ExcludeToggle,
    isSelectAllOption,
    keepTagsOnBackspace,
    pickBesideEmptiness,
    SELECT_ALL_OPTION,
} from './filterSelectParts';
import { useOptionsInactiveMaxRows } from './hooks/useOptionsInactiveMaxRows.ts';

const getOptionLabel = (option: SelectOption) => `${option.label}`;

export const DynamicMultiselect: React.FC<{
    filter: DynamicMultiselectFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    mainFiltersInForm: MainFilterInForm[];
    onChange: (value: SelectOption[], groupSelector: string) => void;
    onExcludingChange?: (isExcluding: boolean, groupSelector: string) => void;
    // Keep the dropdown inside this field (true, default), so in the filter panel — a tall scrollable
    // container — it scrolls with the panel and is clipped by it. Set false when rendered inside an
    // `overflow:hidden` host (e.g. the My Portfolio toolbar) so the popup portals out instead of being
    // clipped to nothing.
    disablePortal?: boolean;
    // Compact mode for tight hosts (e.g. the My Portfolio toolbar): pin the selected-chips field to a
    // single row + "+N" overflow chip and never expand on focus, so the field keeps a fixed height
    // and width instead of wrapping to many rows and overlapping neighbours.
    collapseTags?: boolean;
}> = React.memo(
    ({
        filter,
        onChange,
        onExcludingChange,
        quickFilters,
        mainFilters,
        mainFiltersInForm,
        disablePortal = true,
        collapseTags = false,
    }) => {
        const { paginated } = filter;
        const [fetchData, { data: fetchedData, isLoading: isInitialLoading }] = paginated.fetch();
        const lastNameFirst = useLastNameFirst();
        const fallbackTranslation = t('field.other');
        const onChangeRef = useRef(onChange); // bandaid fix, should be investigated in the future
        onChangeRef.current = onChange;
        const [isExcluding, setIsExcluding] = useState(filter?.isExcluding || false);
        const anchorRef = useComboboxAnchor();
        const mappedFetchedSelectedOptions = useMemo(() => {
            const data = fetchedData?.content || undefined;
            return data
                ? data.map((item) => ({
                      ...item,
                      value: get(item, paginated.getOptionIdentifier),
                      label: optionDisplayLabel(item, lastNameFirst, get(item, paginated.getOptionLabel)),
                  }))
                : undefined;
        }, [fetchedData, paginated.getOptionIdentifier, paginated.getOptionLabel, lastNameFirst]);
        const [inputValue, setInputValue] = useState('');
        const {
            dataToDisplay,
            handleLoadMore,
            handleSearchChange,
            hasNextPage,
            isLoading,
            isError,
            isOpen,
            isOpenPending,
            setOpen,
            dropPendingOpen,
            onInputKeyDown,
        } = usePaginatedPopup<Record<string, unknown>>(
            { ...paginated, quickFilters, mainFilters: mainFiltersInForm },
            () => setInputValue('')
        );
        const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(filter?.selected || []);
        const mappedOptions = useMemo<SelectOption[]>(() => {
            return dataToDisplay.map((data) => ({
                ...data,
                value: get(data, paginated.getOptionIdentifier),
                label: optionDisplayLabel(data, lastNameFirst, get(data, paginated.getOptionLabel)),
            }));
        }, [dataToDisplay, paginated.getOptionIdentifier, paginated.getOptionLabel, lastNameFirst]);
        const getSameNameEmail = useSameNameEmail(mappedOptions);
        const isAllFilteredSelected = useMemo(
            () => !checkIfSomeFilteredOptionNotSelected(selectedOptions, mappedOptions),
            [selectedOptions, mappedOptions]
        );
        const isAllFilteredNotSelected = useMemo(
            () => !checkIfSomeFilteredOptionSelected(selectedOptions, mappedOptions),
            [selectedOptions, mappedOptions]
        );

        const isSelectAllShown = inputValue.length >= MIN_SEARCH_LENGTH_FOR_SELECT_ALL && mappedOptions.length > 0;
        // Beside "is not empty", the two head the list above a divider while nothing is searched.
        const pinned: SelectOption[] =
            filter.nullOption && filter.notEmptyOption && !inputValue
                ? [
                      { value: NULL_FILTER_SENTINEL, label: filter.nullOption.label },
                      { value: NOT_NULL_FILTER_SENTINEL, label: filter.notEmptyOption.label },
                  ]
                : [];

        const listRootWrapperRef = useRef<HTMLDivElement | null>(null);
        const [loaderEl, setLoaderEl] = useState<HTMLDivElement | null>(null);
        useInfiniteScroll(
            loaderEl,
            {
                observer: { root: listRootWrapperRef.current, rootMargin: '75px', threshold: 0 },
                disabled: isError,
                isLoading,
            },
            handleLoadMore
        );

        const handleClearOptions = () => setSelectedOptions([]);
        const handleToggleSelectAll = () => {
            if (mappedOptions.length === mappedOptions.length) {
                const options = selectedOptions.length === mappedOptions.length ? [] : mappedOptions;
                setSelectedOptions(options);
                return options;
            } else {
                let resultOptions: SelectOption[] = [];
                if (isAllFilteredSelected) {
                    setSelectedOptions((prevState) => {
                        const options = separateOptions(prevState, mappedOptions);
                        resultOptions = options;
                        return options;
                    });
                } else {
                    setSelectedOptions((prevState) => {
                        const options = mergeOptions(prevState, mappedOptions);
                        resultOptions = options;
                        return options;
                    });
                }
                return resultOptions;
            }
        };

        // monitor for "clear all filters" essentially
        useEffect(() => {
            if (filter?.selected.length === 0 && selectedOptions.length > 0) {
                handleClearOptions();
            }
        }, [filter?.selected, selectedOptions]);

        // sync isExcluding state with filter prop (render-time prev-value compare)
        const [prevFilterIsExcluding, setPrevFilterIsExcluding] = useState(filter?.isExcluding);
        if (filter?.isExcluding !== prevFilterIsExcluding) {
            setPrevFilterIsExcluding(filter?.isExcluding);
            if (filter?.isExcluding !== isExcluding) {
                setIsExcluding(filter?.isExcluding || false);
            }
        }

        const handleExcludingToggle = (checked: boolean) => {
            setIsExcluding(checked);
            if (onExcludingChange) {
                onExcludingChange(checked, getSelectorKey(filter));
            }
        };

        const commit = (options: SelectOption[]) => {
            setSelectedOptions(options);
            onChange(options, getSelectorKey(filter));
        };

        const handleValueChange = (next: SelectOption[]) => {
            if (next.some(isSelectAllOption)) {
                onChange(handleToggleSelectAll() || [], getSelectorKey(filter));
                return;
            }
            if (filter.notEmptyOption) {
                const { options, dropsExclusion } = pickBesideEmptiness(selectedOptions, next);
                if (dropsExclusion && isExcluding) handleExcludingToggle(false);
                commit(options);
                return;
            }
            commit(next);
        };

        const realSelected = useMemo(
            () => (filter.selected || []).filter((opt) => !isEmptinessOption(opt)),
            [filter.selected]
        );
        const sentinelInSelected = useMemo(() => (filter.selected || []).find(isEmptinessOption), [filter.selected]);

        const unfetchedValues = Boolean(
            realSelected.length > 0 &&
                mappedOptions.find((option) => realSelected.some((opt) => opt.value === option.value))
                ? undefined
                : realSelected.length > 0
        );

        useEffect(() => {
            if (
                unfetchedValues === undefined ||
                !unfetchedValues ||
                isEqual(filter.selected, mappedFetchedSelectedOptions)
            ) {
                return;
            }
            fetchData({
                filters: [
                    paginated.filters,
                    realSelected.length > 0
                        ? `${paginated.getOptionIdentifier}=in=(${realSelected.map((item) => (('' + item.value).startsWith('"') ? item.value : `"${item.value}"`)).join(',')})`
                        : undefined,
                ]
                    .filter(Boolean)
                    .join(';'),
                page: 0,
                pageSize: 1000,
            })
                .then((val) => val.data?.content || undefined)
                .then((data) => {
                    return data !== undefined
                        ? data.map((item) => ({
                              ...item,
                              value: get(item, paginated.getOptionIdentifier),
                              label: optionDisplayLabel(item, lastNameFirst, get(item, paginated.getOptionLabel)),
                          }))
                        : undefined;
                })
                .then((data) => {
                    if (!data || isEqual(filter.selected, data)) {
                        return;
                    }
                    const withSentinel = sentinelInSelected ? [sentinelInSelected, ...data] : data;
                    if (!isEqual(sortBy(selectedOptions, 'id'), sortBy(withSentinel, 'id'))) {
                        setSelectedOptions(withSentinel);
                    }
                    onChangeRef.current(withSentinel, getSelectorKey(filter));
                });
        }, [
            unfetchedValues,
            fetchData,
            filter.selected,
            paginated.filters,
            filter,
            mappedFetchedSelectedOptions,
            paginated.getOptionIdentifier,
            paginated.getOptionLabel,
            selectedOptions,
            realSelected,
            sentinelInSelected,
            lastNameFirst,
        ]);

        const getGroupedBy = (option: SelectOption) => {
            if (!option) {
                return fallbackTranslation;
            }

            if (typeof filter.paginated.groupByField === 'string') {
                return get(option, filter.paginated.groupByField);
            }

            return fallbackTranslation;
        };

        const isGrouped = !!filter.paginated.groupByField;
        // The loaded page is already ordered by the group field, so the groups come out in the order
        // the rows arrive.
        const groupedOptions = useMemo<OptionGroup<SelectOption>[]>(
            () => (isGrouped ? groupConsecutiveOptions(mappedOptions, (option) => String(getGroupedBy(option))) : []),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [mappedOptions, isGrouped, filter.paginated.groupByField, fallbackTranslation]
        );
        // The list's arrow keys and Enter pick from `items` by the row's position, so the select-all row
        // leads them the way it leads the rendered rows (in a group of its own when the list is grouped).
        const comboboxItems = isGrouped
            ? isSelectAllShown
                ? [{ key: String(SELECT_ALL_OPTION.value), value: '', items: [SELECT_ALL_OPTION] }, ...groupedOptions]
                : groupedOptions
            : isSelectAllShown
              ? [SELECT_ALL_OPTION, ...mappedOptions]
              : [...pinned, ...mappedOptions];

        const isHidden =
            filter.getShouldHide !== undefined
                ? filter.getShouldHide({
                      mainFilters,
                      quickFilters,
                      groupSelector: getSelectorKey(filter),
                  })
                : false;

        useEffect(() => {
            // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
            // already-empty filter on every render of a hidden one would loop.
            if (isHidden && !filter?.preserveValueWhenHidden && filter.selected.length > 0) {
                handleClearOptions();
                onChange([], getSelectorKey(filter));
            }
        }, [filter, isHidden, onChange]);

        const { focused, setFocused, hiddenCount, visibleCount, measureRef } = useOptionsInactiveMaxRows({
            selectedOptions,
            // Compact toolbar mode: collapse to a single row + "+N", don't expand on focus, and keep
            // at least one name visible in front of the "+N".
            maxRows: collapseTags ? 1 : undefined,
            expandOnFocus: !collapseTags,
            minVisibleTags: collapseTags ? 1 : 0,
        });

        const { getStartAdornment, getEndAdornment } = useAdornmentGetters(
            filter.getOptionStartAdornment,
            filter.getOptionEndAdornment
        );

        if (isHidden) {
            return null;
        }

        const isSelected = (option: SelectOption) =>
            selectedOptions.some((selected) => selected.value === option.value);

        const renderOptionRow = (option: SelectOption) => {
            const sameNameEmail = getSameNameEmail(option);
            return (
                <CheckboxOptionRow key={String(option.value)} option={option} checked={isSelected(option)}>
                    {/* Label + end-adornment sit snug next to each other (like the form-dropdown option
                    rows). The label truncates with ellipsis when there isn't enough room, keeping the
                    end adornment visible on the right of the name. */}
                    <span className="flex w-full min-w-0 items-center">
                        {filter.getOptionStartAdornment ? (
                            <span className="mr-[.5ch] flex shrink-0 items-center">{getStartAdornment(option)}</span>
                        ) : null}
                        <span className={cn('min-w-0 truncate', sameNameEmail && 'flex-1')}>
                            <PersonOptionLabel
                                option={option}
                                label={getOptionLabel(option)}
                                sameNameEmail={sameNameEmail}
                            />
                        </span>
                        {filter.getOptionEndAdornment ? (
                            <span className="ml-[.5ch] flex shrink-0 items-center">{getEndAdornment(option)}</span>
                        ) : null}
                    </span>
                </CheckboxOptionRow>
            );
        };

        const emptinessToggle = (value: string, label: string) => (
            <label className="flex cursor-pointer items-center gap-1 select-none">
                <Checkbox
                    checked={selectedOptions.some((o) => o.value === value)}
                    onCheckedChange={(checked) => {
                        const others = selectedOptions.filter((o) => o.value !== value);
                        commit(checked ? [{ value, label }, ...others] : others);
                    }}
                />
                <span className="text-caption text-fg-secondary">{label}</span>
            </label>
        );

        return (
            <div
                // `min-w-0` in compact mode stops the flex chain from growing to the chips' min-content
                // width, so the field stays within its fixed-width host.
                className={cn('flex grow flex-col gap-2', collapseTags && 'min-w-0')}
                key={`filter-${filter.selector}`}
            >
                {filter.label && (
                    <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues}>
                        {filter.canExclude && (
                            <ExcludeToggle
                                checked={isExcluding}
                                onChange={handleExcludingToggle}
                                disabled={!!filter.notEmptyOption && !!sentinelInSelected}
                                tooltip={filter.controlTooltips?.exclude}
                            />
                        )}
                    </FilterLabel>
                )}
                {filter.nullOption && !filter.notEmptyOption && (
                    <div className="flex items-center gap-3">
                        {emptinessToggle(NULL_FILTER_SENTINEL, filter.nullOption.label)}
                    </div>
                )}
                {/* The popup portals into the nearest `data-form-body`: this field's own box when the
                    dropdown is to stay inline, else the page's form body or <body>. */}
                <div className={cn('relative', collapseTags && 'min-w-0')} data-form-body={disablePortal || undefined}>
                    <Combobox<SelectOption, true>
                        multiple
                        items={comboboxItems as readonly SelectOption[]}
                        value={selectedOptions}
                        disabled={isInitialLoading}
                        // Search runs on the server, so the popup shows exactly the page it was handed.
                        filter={null}
                        itemToStringLabel={getOptionLabel}
                        isItemEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                        inputValue={inputValue}
                        // Only typing changes the search: picking an option keeps it, so several matches
                        // can be picked in turn.
                        onInputValueChange={(value, details) => {
                            if (details.reason !== 'input-change' && details.reason !== 'input-clear') return;
                            setInputValue(value);
                            handleSearchChange(value);
                        }}
                        open={isOpen}
                        onOpenChange={(next, details) => {
                            // Picking an option — the select-all row included — keeps the list open.
                            if (!next && details.reason === 'item-press') return;
                            setOpen(next);
                        }}
                        onValueChange={handleValueChange}
                    >
                        {/* Compact mode: clamp the value field to a single row. `nowrap` keeps chips +
                            "+N" + input on one line and `overflow:hidden` both clips the overflow and
                            collapses the row's min-content so the host's fixed width is respected. */}
                        <ComboboxChips
                            ref={anchorRef}
                            className={cn(
                                'bg-paper relative pr-14 has-data-[slot=combobox-chip]:pr-14 has-data-[slot=combobox-chip]:pl-2.5',
                                collapseTags && 'min-w-0 flex-nowrap overflow-hidden'
                            )}
                        >
                            <CollapsedTags
                                comboboxChips
                                values={selectedOptions}
                                // Unresolved label (id seeded from URL, name pending) shows a skeleton,
                                // not the raw id.
                                getLabel={(option) => {
                                    const selected = option as SelectOption;
                                    return selected.label ? (
                                        getOptionLabel(selected)
                                    ) : (
                                        <Skeleton className="inline-block h-3.5 w-[70px] align-middle" />
                                    );
                                }}
                                onDelete={(index) =>
                                    commit(selectedOptions.filter((_, position) => position !== index))
                                }
                                // Compact (toolbar) mode never expands on focus, and caps each chip's
                                // width so a long name ellipsizes instead of widening the field.
                                showAll={focused && !collapseTags}
                                visibleCount={visibleCount}
                                hiddenCount={hiddenCount}
                                measureRef={measureRef}
                                disabled={isInitialLoading}
                                tagClassName={collapseTags ? 'max-w-40' : undefined}
                            />
                            <ComboboxChipsInput
                                id={`dynamic-multi-autocomplete-${filter.selector}`}
                                aria-label={filter.label}
                                placeholder={t('search')}
                                aria-busy={isOpenPending || undefined}
                                onFocus={() => setFocused(true)}
                                onBlur={() => {
                                    setFocused(false);
                                    dropPendingOpen();
                                }}
                                onKeyDown={(event) => {
                                    keepTagsOnBackspace(event);
                                    onInputKeyDown(event);
                                }}
                            />
                            {/* Pinned to the field's right edge, clear of the tags, as the list's own buttons were.
                                While the first page loads a spinner takes the chevron's slot. */}
                            <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
                                <ComboboxClear className="size-5" />
                                {isOpenPending ? (
                                    <CircularLoader className="static size-5 [&>span]:size-4" />
                                ) : (
                                    <InputGroupButton
                                        size="icon-xs"
                                        variant="ghost"
                                        render={<ComboboxTrigger />}
                                        tabIndex={-1}
                                        className="size-5 data-pressed:bg-transparent"
                                        disabled={isInitialLoading}
                                    />
                                )}
                            </div>
                        </ComboboxChips>
                        <ComboboxContent anchor={anchorRef} className="shadow-dropdown">
                            <ComboboxEmpty>{isLoading ? t('loading') : t('no-options')}</ComboboxEmpty>
                            {/* Mapped rather than handed to the list's function child: filtering is off
                                here, and the select-all and load-more rows have to sit inside the scroll
                                box, which the function form leaves no room for. */}
                            <ComboboxList ref={listRootWrapperRef}>
                                {isSelectAllShown ? (
                                    <CheckboxOptionRow
                                        option={SELECT_ALL_OPTION}
                                        checked={isAllFilteredSelected}
                                        indeterminate={!isAllFilteredSelected && !isAllFilteredNotSelected}
                                    >
                                        {getOptionLabel(SELECT_ALL_OPTION)}
                                    </CheckboxOptionRow>
                                ) : null}
                                {pinned.length ? (
                                    <>
                                        {pinned.map((option) => (
                                            <CheckboxOptionRow
                                                key={String(option.value)}
                                                option={option}
                                                checked={isSelected(option)}
                                            >
                                                {getOptionLabel(option)}
                                            </CheckboxOptionRow>
                                        ))}
                                        <ComboboxSeparator />
                                    </>
                                ) : null}
                                {isGrouped
                                    ? groupedOptions.map((group) => (
                                          <ComboboxGroup key={group.key} items={group.items}>
                                              <ComboboxLabel>{group.value}</ComboboxLabel>
                                              <ComboboxCollection>
                                                  {(option: SelectOption) => renderOptionRow(option)}
                                              </ComboboxCollection>
                                          </ComboboxGroup>
                                      ))
                                    : mappedOptions.map((option) => renderOptionRow(option))}
                                {hasNextPage ? (
                                    <div ref={setLoaderEl} className="relative py-2">
                                        <CircularLoader className="static [&>span]:size-6" />
                                    </div>
                                ) : null}
                            </ComboboxList>
                        </ComboboxContent>
                    </Combobox>
                </div>
            </div>
        );
    }
);
